/*
  Google Reviews — the manager-controlled review-request workspace.

  Layout (single page, 4 tabs):
    * Dashboard — metrics + reconcile / worker actions
    * Approvals — pending drafts, preview, edit, approve, reject
    * Triggers  — CRUD + activate/deactivate
    * Settings  — review URL, quiet hours, daily cap, templates

  Manager control is baked in: draft edits invalidate approval; the
  send worker never fires without a manager-approved matching hash.
*/
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Star, ShieldCheck, X, CheckCircle2, Ban, Send, Loader2,
  RefreshCw, Plus, Trash2, Save, Edit3, ArrowLeft, PenLine, Sparkles,
  ClipboardList, Sliders, Zap,
} from "lucide-react";
import { RejectReasonDialog, RejectReasonsCard } from "@/components/reviews/RejectReasons";
import { cn } from "@/lib/utils";

const STATE_STYLES = {
  candidate: "bg-zinc-100 text-zinc-700 border-zinc-200",
  needs_approval: "bg-amber-50 text-amber-800 border-amber-200",
  approved: "bg-emerald-50 text-emerald-800 border-emerald-200",
  queued: "bg-blue-50 text-blue-800 border-blue-200",
  sent: "bg-blue-100 text-blue-900 border-blue-300",
  delivered: "bg-emerald-100 text-emerald-900 border-emerald-300",
  failed: "bg-rose-50 text-rose-800 border-rose-200",
  rejected: "bg-zinc-100 text-zinc-500 border-zinc-200 line-through",
  cancelled: "bg-zinc-100 text-zinc-500 border-zinc-200 line-through",
  reminder_needs_approval: "bg-amber-100 text-amber-900 border-amber-300",
  reminder_approved: "bg-emerald-100 text-emerald-900 border-emerald-300",
  reminder_sent: "bg-blue-100 text-blue-900 border-blue-300",
  reminder_delivered: "bg-emerald-200 text-emerald-900 border-emerald-400",
  reminder_failed: "bg-rose-100 text-rose-900 border-rose-300",
};

export default function GoogleReviews() {
  const [tab, setTab] = useState("dashboard");
  return (
    <div className="p-6 space-y-4" data-testid="reviews-page">
      <div className="flex items-center gap-3">
        <div className="rounded bg-amber-100 p-2">
          <Star className="h-5 w-5 text-amber-600" fill="currentColor" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900">Google Reviews</h1>
          <p className="text-sm text-zinc-500">
            Manager-approved review requests — nothing sends without you.
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="grid grid-cols-4 md:w-[560px]">
          <TabsTrigger value="dashboard" data-testid="reviews-tab-dashboard">
            <Sparkles className="h-3.5 w-3.5 mr-1" /> Dashboard
          </TabsTrigger>
          <TabsTrigger value="approvals" data-testid="reviews-tab-approvals">
            <ClipboardList className="h-3.5 w-3.5 mr-1" /> Approvals
          </TabsTrigger>
          <TabsTrigger value="triggers" data-testid="reviews-tab-triggers">
            <Zap className="h-3.5 w-3.5 mr-1" /> Triggers
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="reviews-tab-settings">
            <Sliders className="h-3.5 w-3.5 mr-1" /> Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard"><DashboardPane /></TabsContent>
        <TabsContent value="approvals"><ApprovalsPane /></TabsContent>
        <TabsContent value="triggers"><TriggersPane /></TabsContent>
        <TabsContent value="settings"><SettingsPane /></TabsContent>
      </Tabs>
    </div>
  );
}


/* ------------------------------------------------------------------------ */
/*  Dashboard                                                                */
/* ------------------------------------------------------------------------ */
function DashboardPane() {
  const [metrics, setMetrics] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const r = await api.get("/reviews/metrics");
      setMetrics(r.data);
    } catch (e) {
      toast.error("Couldn't load metrics", { description: e?.response?.data?.detail || e.message });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function reconcile() {
    setBusy(true);
    try {
      const r = await api.post("/reviews/reconcile");
      const totals = r.data?.totals || {};
      toast.success(`Reconciled — ${totals.new_candidates || 0} new, ${totals.promoted_to_needs_approval || 0} needing approval`);
      await load();
    } catch (e) {
      toast.error("Reconcile failed", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  }
  async function runWorker() {
    setBusy(true);
    try {
      const r = await api.post("/reviews/worker/tick");
      const d = r.data || {};
      if (d.skipped) {
        toast.info(`Worker skipped: ${d.skipped}`);
      } else {
        toast.success(`Worker: sent ${d.sent || 0}, failed ${d.failed || 0}, invalidated ${d.invalidated || 0}`);
      }
      await load();
    } catch (e) {
      toast.error("Worker tick failed", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  }
  async function prepareReminders() {
    setBusy(true);
    try {
      const r = await api.post("/reviews/reminders/prepare");
      toast.success(`Prepared ${r.data?.prepared || 0} reminder drafts`);
      await load();
    } catch (e) {
      toast.error("Reminder prep failed", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  }

  const byState = metrics?.by_state || {};
  const byService = metrics?.by_service_last_30d || {};

  return (
    <div className="mt-4 space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard label="Pending approval" value={metrics?.approvals_pending || 0} accent="amber"
                    testId="metric-approvals-pending" />
        <MetricCard label="Sent (last 30d)" value={metrics?.sent_last_30d || 0} accent="emerald"
                    testId="metric-sent" />
        <MetricCard label="Failed (last 30d)" value={metrics?.failed_last_30d || 0} accent="rose"
                    testId="metric-failed" />
        <MetricCard label="Active" value={(byState.approved || 0) + (byState.queued || 0)} accent="blue"
                    testId="metric-active" />
      </div>

      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={reconcile} disabled={busy} data-testid="dashboard-reconcile">
          {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
          Reconcile eligibility now
        </Button>
        <Button variant="outline" onClick={runWorker} disabled={busy} data-testid="dashboard-run-worker">
          <Send className="h-3.5 w-3.5 mr-1" /> Send worker tick
        </Button>
        <Button variant="outline" onClick={prepareReminders} disabled={busy} data-testid="dashboard-prepare-reminders">
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Prepare reminders now
        </Button>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Requests by service (last 30 days)</CardTitle></CardHeader>
        <CardContent>
          {Object.keys(byService).length === 0 ? (
            <div className="text-sm text-zinc-500">Nothing here yet — reconcile above once you've activated a trigger.</div>
          ) : (
            <ul className="text-sm space-y-1">
              {Object.entries(byService).map(([svc, n]) => (
                <li key={svc} className="flex justify-between border-b border-zinc-100 py-1">
                  <span className="text-zinc-700 capitalize">{svc?.replace(/_/g, " ")}</span>
                  <span className="font-medium tabular-nums">{n}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">By state</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {Object.entries(byState).map(([s, n]) => (
              <Badge key={s} variant="outline"
                     className={cn("text-xs", STATE_STYLES[s] || "border-zinc-200")}>
                {s.replace(/_/g, " ")} · {n}
              </Badge>
            ))}
            {Object.keys(byState).length === 0 && (
              <div className="text-sm text-zinc-500">No candidates yet.</div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


function MetricCard({ label, value, accent = "zinc", testId }) {
  const map = {
    amber: "border-amber-200 bg-amber-50",
    emerald: "border-emerald-200 bg-emerald-50",
    rose: "border-rose-200 bg-rose-50",
    blue: "border-blue-200 bg-blue-50",
    zinc: "border-zinc-200 bg-zinc-50",
  };
  return (
    <div className={cn("rounded border p-3", map[accent])} data-testid={testId}>
      <div className="text-xs text-zinc-600 uppercase">{label}</div>
      <div className="text-2xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}


/* ------------------------------------------------------------------------ */
/*  Approvals                                                                */
/* ------------------------------------------------------------------------ */
function ApprovalsPane() {
  const [filter, setFilter] = useState("needs_approval");
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [rejectReasons, setRejectReasons] = useState([]);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectBusy, setRejectBusy] = useState(false);

  useEffect(() => {
    api.get("/reviews/settings").then((r) => setRejectReasons(r.data?.reject_reasons || [])).catch(() => {});
  }, []);
  useEffect(() => { setSelected(new Set()); }, [filter]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter === "all" ? "" : `?state=${filter}`;
      const r = await api.get(`/reviews/candidates${q}`);
      setCandidates(r.data?.candidates || []);
    } catch (e) {
      toast.error("Couldn't load candidates", { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  function toggleSel(id) {
    setSelected((prev) => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  }

  async function bulkApprove() {
    if (selected.size === 0) return;
    if (!window.confirm(`Approve ${selected.size} draft(s)? Sends still respect quiet hours + daily cap.`)) return;
    try {
      const r = await api.post("/reviews/candidates/bulk-approve", {
        candidate_ids: [...selected],
      });
      const ok = (r.data?.results || []).filter((x) => x.ok).length;
      toast.success(`Approved ${ok} of ${selected.size}`);
      setSelected(new Set());
      await load();
    } catch (e) {
      toast.error("Bulk approve failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  const selectable = candidates.filter(isPendingCandidate);
  const allSelected = selectable.length > 0 && selectable.every((c) => selected.has(c.id));
  const someSelected = selectable.some((c) => selected.has(c.id));
  function toggleAll() {
    setSelected(allSelected ? new Set() : new Set(selectable.map((c) => c.id)));
  }

  async function bulkReject(reason, note) {
    if (selected.size === 0) return;
    setRejectBusy(true);
    try {
      const r = await api.post("/reviews/candidates/bulk-reject", {
        candidate_ids: [...selected], reason, note,
      });
      setRejectOpen(false);
      const results = r.data?.results || [];
      const ok = results.filter((x) => x.ok).length;
      const skipped = results.length - ok;
      toast.success(`Rejected ${ok} of ${selected.size}`, {
        description: skipped ? `${skipped} skipped — already sent, rejected or cancelled` : undefined,
      });
      setSelected(new Set());
      await load();
    } catch (e) {
      toast.error("Bulk reject failed", { description: e?.response?.data?.detail || e.message });
    } finally { setRejectBusy(false); }
  }

  const stateFilters = [
    ["needs_approval", "Needs approval"],
    ["reminder_needs_approval", "Reminders"],
    ["approved", "Approved"],
    ["sent", "Sent"],
    ["failed", "Failed"],
    ["rejected", "Rejected"],
    ["all", "All"],
  ];

  return (
    <div className="mt-4 space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        {stateFilters.map(([k, label]) => (
          <button key={k}
            onClick={() => setFilter(k)}
            data-testid={`approvals-filter-${k}`}
            className={cn(
              "text-xs px-2 py-1 rounded border transition",
              filter === k
                ? "border-indigo-500 bg-indigo-50 text-indigo-800"
                : "border-zinc-200 hover:bg-zinc-50",
            )}>
            {label}
          </button>
        ))}
        <div className="flex-1" />
        {selected.size > 0 && (
          <>
            <Button size="sm" onClick={bulkApprove} data-testid="approvals-bulk-approve">
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve {selected.size} selected
            </Button>
            <Button size="sm" variant="outline" onClick={() => setRejectOpen(true)}
                    className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="approvals-bulk-reject">
              <X className="h-3.5 w-3.5 mr-1" /> Reject {selected.size} selected
            </Button>
          </>
        )}
        <Button size="sm" variant="ghost" onClick={load}>
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500 py-8 text-center">
          <Loader2 className="h-4 w-4 inline mr-1 animate-spin" /> Loading…
        </div>
      ) : candidates.length === 0 ? (
        <div className="text-sm text-zinc-500 py-8 text-center border rounded"
             data-testid="approvals-empty">
          Nothing here. Try reconciling from the Dashboard tab.
        </div>
      ) : (
        <div className="space-y-2">
          {selectable.length > 0 && (
            <label className="flex items-center gap-2 px-2 text-xs text-zinc-600 cursor-pointer w-fit" data-testid="approvals-select-all-label">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                onChange={toggleAll}
                className="h-4 w-4 accent-indigo-600"
                data-testid="approvals-select-all"
              />
              {allSelected ? `All ${selectable.length} selected` : `Select all ${selectable.length} awaiting approval`}
            </label>
          )}
          {candidates.map((c) => (
            <CandidateRow key={c.id} c={c}
                            selected={selected.has(c.id)}
                            onToggleSel={() => toggleSel(c.id)}
                            onOpen={() => setOpenId(c.id)} />
          ))}
        </div>
      )}

      {openId && (
        <CandidateDrawer
          candidateId={openId}
          rejectReasons={rejectReasons}
          onClose={() => setOpenId(null)}
          onChanged={() => { load(); }}
        />
      )}
      {rejectOpen && (
        <RejectReasonDialog
          count={selected.size}
          reasons={rejectReasons}
          busy={rejectBusy}
          onCancel={() => setRejectOpen(false)}
          onConfirm={bulkReject}
        />
      )}
    </div>
  );
}


const isPendingCandidate = (c) => c.state === "needs_approval" || c.state === "reminder_needs_approval";

function CandidateRow({ c, selected, onToggleSel, onOpen }) {
  const isPending = isPendingCandidate(c);
  return (
    <div className="border rounded p-2 flex items-center gap-2"
         data-testid={`candidate-row-${c.id}`}>
      {isPending && (
        <input type="checkbox" checked={selected} onChange={onToggleSel}
                className="h-4 w-4 accent-indigo-600 flex-shrink-0"
                data-testid={`candidate-select-${c.id}`}
                onClick={(e) => e.stopPropagation()} />
      )}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate flex items-center gap-2">
          <span className="truncate">{c.client_display_name || c.client_email}</span>
          <Badge variant="outline" className={cn("text-2xs", STATE_STYLES[c.state])}>
            {c.state?.replace(/_/g, " ")}
          </Badge>
          {c.manual && <Badge variant="outline" className="text-2xs">manual</Badge>}
        </div>
        <div className="text-2xs text-zinc-500 truncate">
          {c.service?.replace(/_/g, " ")} · {c.channel}
          {c.draft?.destination ? ` → ${c.draft.destination}` : ""}
          {c.last_block_reason ? ` · blocked: ${c.last_block_reason}` : ""}
        </div>
        {c.state === "rejected" && (c.reject_reason || c.reject_note) && (
          <div className="text-2xs text-rose-700 truncate" data-testid={`candidate-reject-reason-${c.id}`}>
            Rejected: {[c.reject_reason, c.reject_note].filter(Boolean).join(" — ")}
          </div>
        )}
      </div>
      <Button size="sm" variant="outline" onClick={onOpen}
              data-testid={`candidate-open-${c.id}`}>
        Open
      </Button>
    </div>
  );
}


function CandidateDrawer({ candidateId, rejectReasons, onClose, onChanged }) {
  const [c, setC] = useState(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [events, setEvents] = useState([]);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/reviews/candidates/${candidateId}`);
      setC(r.data);
      const e = await api.get(`/reviews/events?candidate_id=${candidateId}`);
      setEvents(e.data?.events || []);
    } catch (e) {
      toast.error("Couldn't load candidate", { description: e?.response?.data?.detail || e.message });
    }
  }, [candidateId]);
  useEffect(() => { load(); }, [load]);

  async function save() {
    setBusy(true);
    try {
      await api.patch(`/reviews/candidates/${candidateId}/draft`, {
        channel: c.draft?.channel, destination: c.draft?.destination,
        subject: c.draft?.subject, body: c.draft?.body,
      });
      toast.success("Draft saved");
      setEditing(false);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  }
  async function approve() {
    if (!window.confirm("Approve this request? The send worker will dispatch during business hours + within the daily cap.")) return;
    setBusy(true);
    try {
      await api.post(`/reviews/candidates/${candidateId}/approve`);
      toast.success("Approved");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error("Approve failed", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  }
  async function reject(reason, note) {
    setBusy(true);
    try {
      await api.post(`/reviews/candidates/${candidateId}/reject`, { reason, note });
      setRejectOpen(false);
      toast.success("Rejected");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error("Reject failed", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  }
  async function cancel() {
    if (!window.confirm("Cancel this pending request?")) return;
    setBusy(true);
    try {
      await api.post(`/reviews/candidates/${candidateId}/cancel`);
      toast.success("Cancelled");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error("Cancel failed", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  }

  if (!c) {
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Loading…</DialogTitle></DialogHeader>
          <Loader2 className="h-6 w-6 mx-auto my-8 animate-spin" />
        </DialogContent>
      </Dialog>
    );
  }

  const draft = c.draft || {};
  const canEdit = ["needs_approval", "reminder_needs_approval", "approved", "reminder_approved"].includes(c.state);
  const canApprove = c.state === "needs_approval" || c.state === "reminder_needs_approval";
  const canCancel = ["needs_approval", "approved", "queued", "reminder_needs_approval", "reminder_approved"].includes(c.state);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl w-[95vw] max-h-[92vh] overflow-y-auto"
                     data-testid="candidate-drawer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-indigo-700" />
            {c.client_display_name || c.client_email}
            <Badge variant="outline" className={cn("text-2xs", STATE_STYLES[c.state])}>
              {c.state?.replace(/_/g, " ")}
            </Badge>
          </DialogTitle>
          <DialogDescription>
            Service: <b>{c.service?.replace(/_/g, " ") || "—"}</b> · Channel: <b>{c.channel}</b>
            {c.eligibility?.count_at_trigger != null &&
              <> · Completed sessions at trigger: <b>{c.eligibility.count_at_trigger}</b></>}
            {c.manual && <> · <b>Manually nominated</b></>}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-2xs uppercase text-zinc-500">Channel</label>
              {editing ? (
                <select value={draft.channel || "sms"}
                        onChange={(e) => setC({ ...c, draft: { ...draft, channel: e.target.value } })}
                        className="w-full h-8 text-sm border rounded px-2">
                  <option value="sms">SMS</option>
                  <option value="email">Email</option>
                </select>
              ) : (
                <div className="text-sm">{draft.channel}</div>
              )}
            </div>
            <div>
              <label className="text-2xs uppercase text-zinc-500">Destination</label>
              {editing ? (
                <Input value={draft.destination || ""}
                        onChange={(e) => setC({ ...c, draft: { ...draft, destination: e.target.value } })} />
              ) : (
                <div className="text-sm font-mono">{draft.destination || "—"}</div>
              )}
            </div>
          </div>
          {draft.channel === "email" && (
            <div>
              <label className="text-2xs uppercase text-zinc-500">Subject</label>
              {editing ? (
                <Input value={draft.subject || ""}
                        onChange={(e) => setC({ ...c, draft: { ...draft, subject: e.target.value } })} />
              ) : (
                <div className="text-sm">{draft.subject || "—"}</div>
              )}
            </div>
          )}
          <div>
            <label className="text-2xs uppercase text-zinc-500">Body</label>
            {editing ? (
              <Textarea rows={8} value={draft.body || ""}
                          data-testid="candidate-drawer-body"
                          onChange={(e) => setC({ ...c, draft: { ...draft, body: e.target.value } })} />
            ) : (
              <pre className="whitespace-pre-wrap text-sm bg-zinc-50 border rounded p-2 max-h-64 overflow-y-auto">
                {draft.body || "—"}
              </pre>
            )}
            {editing && (
              <div className="text-2xs text-zinc-500 mt-1">
                Chars: {(draft.body || "").length} · Any edit invalidates the current approval.
              </div>
            )}
          </div>

          <div className="border-t pt-2">
            <div className="text-2xs uppercase text-zinc-500 mb-1">Audit trail</div>
            <ul className="text-xs space-y-1 max-h-40 overflow-y-auto">
              {events.map((e) => (
                <li key={e.id} className="border-b border-zinc-100 py-1">
                  <span className="font-medium">{e.kind}</span>
                  <span className="text-zinc-500 ml-2">
                    {new Date(e.at).toLocaleString()} · {e.by?.name || e.by?.id || "system"}
                  </span>
                  {e.detail && Object.keys(e.detail).length > 0 && (
                    <div className="text-zinc-500 ml-3 font-mono truncate">
                      {JSON.stringify(e.detail)}
                    </div>
                  )}
                </li>
              ))}
              {events.length === 0 && <li className="text-zinc-500">No events yet.</li>}
            </ul>
          </div>
        </div>

        <DialogFooter>
          <div className="flex items-center gap-2 w-full flex-wrap">
            {canEdit && !editing && (
              <Button variant="outline" onClick={() => setEditing(true)} disabled={busy}
                      data-testid="candidate-drawer-edit">
                <Edit3 className="h-3.5 w-3.5 mr-1" /> Edit draft
              </Button>
            )}
            {editing && (
              <>
                <Button variant="outline" onClick={() => { setEditing(false); load(); }}>Discard</Button>
                <Button onClick={save} disabled={busy} data-testid="candidate-drawer-save">
                  {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                  Save draft
                </Button>
              </>
            )}
            <div className="flex-1" />
            {canCancel && (
              <Button variant="ghost" onClick={cancel} disabled={busy} className="text-zinc-500">
                <Ban className="h-3.5 w-3.5 mr-1" /> Cancel
              </Button>
            )}
            {canApprove && (
              <>
                <Button variant="outline" onClick={() => setRejectOpen(true)} disabled={busy}
                        className="text-rose-600" data-testid="candidate-drawer-reject">
                  <X className="h-3.5 w-3.5 mr-1" /> Reject
                </Button>
                <Button onClick={approve} disabled={busy || editing}
                        data-testid="candidate-drawer-approve">
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve
                </Button>
              </>
            )}
            <Button variant="ghost" onClick={onClose}>Close</Button>
          </div>
        </DialogFooter>
        {rejectOpen && (
          <RejectReasonDialog
            count={1}
            reasons={rejectReasons}
            busy={busy}
            onCancel={() => setRejectOpen(false)}
            onConfirm={reject}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}


/* ------------------------------------------------------------------------ */
/*  Triggers                                                                 */
/* ------------------------------------------------------------------------ */
const TRIGGER_TYPES = [
  { v: "nth_visit", l: "Nth completed visit" },
  { v: "nth_visit_or_days", l: "Nth visit OR window since first visit" },
];

function TriggersPane() {
  const [triggers, setTriggers] = useState([]);
  const [apptTypes, setApptTypes] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, a, tp] = await Promise.all([
        api.get("/reviews/triggers"),
        api.get("/appointment-types").catch(() => ({ data: [] })),
        api.get("/reviews/templates"),
      ]);
      setTriggers(t.data?.triggers || []);
      setApptTypes(a.data || []);
      setTemplates(tp.data?.templates || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function toggleActive(t) {
    try {
      await api.patch(`/reviews/triggers/${t.id}`, { active: !t.active });
      await load();
    } catch (e) {
      toast.error("Toggle failed", { description: e?.response?.data?.detail || e.message });
    }
  }
  async function save(t) {
    try {
      const body = {
        name: t.name, service: t.service, trigger_type: t.trigger_type,
        threshold: parseInt(t.threshold, 10) || 1,
        or_days_from_first: t.or_days_from_first ? parseInt(t.or_days_from_first, 10) : undefined,
        or_days_from_first_max: t.or_days_from_first_max ? parseInt(t.or_days_from_first_max, 10) : undefined,
        min_days_after_last: parseInt(t.min_days_after_last, 10) || 0,
        channel: t.channel, appointment_type_ids: (t.appointment_type_ids || []).map((x) => parseInt(x, 10)),
        template_initial: t.template_initial, template_reminder: t.template_reminder,
        cooldown_days: parseInt(t.cooldown_days, 10) || 365,
        reminder_days: parseInt(t.reminder_days, 10) || 7,
        daily_cap: parseInt(t.daily_cap, 10) || 20,
        priority: parseInt(t.priority, 10) || 5,
        active: !!t.active,
      };
      if (t.__new) {
        await api.post("/reviews/triggers", body);
      } else {
        await api.patch(`/reviews/triggers/${t.id}`, body);
      }
      toast.success("Trigger saved");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    }
  }
  async function del(t) {
    if (!window.confirm(`Delete trigger "${t.name}"? (Soft-delete — historical candidates remain.)`)) return;
    try {
      await api.delete(`/reviews/triggers/${t.id}`);
      await load();
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || e.message });
    }
  }
  function addNew() {
    setEditing({
      __new: true, name: "New trigger", service: "",
      trigger_type: "nth_visit", threshold: 5,
      min_days_after_last: 1, channel: "sms",
      appointment_type_ids: [],
      template_initial: "default-initial-sms",
      template_reminder: "default-reminder-sms",
      cooldown_days: 365, reminder_days: 7, daily_cap: 20, priority: 5,
      active: false,
    });
  }

  return (
    <div className="mt-4 space-y-3">
      <div className="flex justify-between items-center">
        <div className="text-sm text-zinc-500">
          Rules that decide when a client becomes eligible for a review request. Every trigger is disabled by default.
        </div>
        <Button size="sm" onClick={addNew} data-testid="triggers-new">
          <Plus className="h-3.5 w-3.5 mr-1" /> New trigger
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500 py-6 text-center">
          <Loader2 className="h-4 w-4 inline mr-1 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="space-y-2">
          {triggers.map((t) => (
            <div key={t.id} className="border rounded p-2 flex items-center gap-2"
                 data-testid={`trigger-row-${t.id}`}>
              <label className="flex items-center gap-1 text-xs">
                <input type="checkbox" checked={!!t.active}
                        onChange={() => toggleActive(t)}
                        className="h-4 w-4 accent-indigo-600"
                        data-testid={`trigger-active-${t.id}`} />
                {t.active ? "Active" : "Off"}
              </label>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate flex items-center gap-1">
                  {t.name}
                  {t.is_seeded && <Badge variant="outline" className="text-2xs">seed</Badge>}
                  <Badge variant="outline" className="text-2xs">v{t.version}</Badge>
                </div>
                <div className="text-2xs text-zinc-500 truncate">
                  {t.trigger_type} · threshold {t.threshold}
                  {t.or_days_from_first ? ` · or ${t.or_days_from_first}–${t.or_days_from_first_max}d` : ""}
                  · {t.channel} · {(t.appointment_type_ids || []).length} appt type(s)
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => setEditing({ ...t })}
                      data-testid={`trigger-edit-${t.id}`}>
                <Edit3 className="h-3.5 w-3.5 mr-1" /> Edit
              </Button>
              <Button size="sm" variant="ghost" onClick={() => del(t)}
                      className="text-red-600">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <TriggerEditor
          trigger={editing}
          apptTypes={apptTypes}
          templates={templates}
          onChange={setEditing}
          onSave={() => save(editing)}
          onCancel={() => setEditing(null)}
        />
      )}
    </div>
  );
}


function TriggerEditor({ trigger, apptTypes, templates, onChange, onSave, onCancel }) {
  const t = trigger;
  const smsTpls = templates.filter((x) => x.channel === "sms");
  const emailTpls = templates.filter((x) => x.channel === "email");
  const initialTpls = t.channel === "email" ? emailTpls : smsTpls;
  return (
    <Dialog open onOpenChange={onCancel}>
      <DialogContent className="max-w-2xl w-[95vw] max-h-[92vh] overflow-y-auto"
                     data-testid="trigger-editor-dialog">
        <DialogHeader>
          <DialogTitle>{t.__new ? "New trigger" : `Edit — ${t.name}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <Field label="Name">
              <Input value={t.name || ""} onChange={(e) => onChange({ ...t, name: e.target.value })}
                      data-testid="trigger-name" />
            </Field>
            <Field label="Service (label)">
              <Input value={t.service || ""} onChange={(e) => onChange({ ...t, service: e.target.value })}
                      placeholder="e.g. reformer_pilates" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Trigger type">
              <select value={t.trigger_type} onChange={(e) => onChange({ ...t, trigger_type: e.target.value })}
                        className="w-full h-8 text-sm border rounded px-2">
                {TRIGGER_TYPES.map((x) => <option key={x.v} value={x.v}>{x.l}</option>)}
              </select>
            </Field>
            <Field label="Threshold (Nth visit)">
              <Input type="number" value={t.threshold || 0}
                      onChange={(e) => onChange({ ...t, threshold: e.target.value })} />
            </Field>
          </div>
          {t.trigger_type === "nth_visit_or_days" && (
            <div className="grid grid-cols-2 gap-2">
              <Field label="Days from first visit (min)">
                <Input type="number" value={t.or_days_from_first || 28}
                        onChange={(e) => onChange({ ...t, or_days_from_first: e.target.value })} />
              </Field>
              <Field label="Days from first visit (max)">
                <Input type="number" value={t.or_days_from_first_max || 42}
                        onChange={(e) => onChange({ ...t, or_days_from_first_max: e.target.value })} />
              </Field>
            </div>
          )}
          <div className="grid grid-cols-3 gap-2">
            <Field label="Min days after last">
              <Input type="number" value={t.min_days_after_last || 0}
                      onChange={(e) => onChange({ ...t, min_days_after_last: e.target.value })} />
            </Field>
            <Field label="Channel">
              <select value={t.channel} onChange={(e) => onChange({ ...t, channel: e.target.value })}
                        className="w-full h-8 text-sm border rounded px-2">
                <option value="sms">SMS</option>
                <option value="email">Email</option>
              </select>
            </Field>
            <Field label="Daily cap">
              <Input type="number" value={t.daily_cap || 20}
                      onChange={(e) => onChange({ ...t, daily_cap: e.target.value })} />
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Field label="Cooldown (days)">
              <Input type="number" value={t.cooldown_days || 365}
                      onChange={(e) => onChange({ ...t, cooldown_days: e.target.value })} />
            </Field>
            <Field label="Reminder after (days)">
              <Input type="number" value={t.reminder_days || 7}
                      onChange={(e) => onChange({ ...t, reminder_days: e.target.value })} />
            </Field>
            <Field label="Priority">
              <Input type="number" value={t.priority || 5}
                      onChange={(e) => onChange({ ...t, priority: e.target.value })} />
            </Field>
          </div>
          <Field label="Appointment types (Acuity)">
            <select multiple value={(t.appointment_type_ids || []).map(String)}
                      onChange={(e) => onChange({
                        ...t, appointment_type_ids: Array.from(e.target.selectedOptions).map((o) => parseInt(o.value, 10)),
                      })}
                      className="w-full h-32 text-sm border rounded px-2"
                      data-testid="trigger-appt-types">
              {apptTypes.map((at) => (
                <option key={at.id} value={at.id}>{at.name} (#{at.id})</option>
              ))}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Initial template">
              <select value={t.template_initial || ""}
                        onChange={(e) => onChange({ ...t, template_initial: e.target.value })}
                        className="w-full h-8 text-sm border rounded px-2">
                <option value="">— pick —</option>
                {initialTpls.filter((x) => x.kind === "initial").map((x) => (
                  <option key={x.id} value={x.id}>{x.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Reminder template">
              <select value={t.template_reminder || ""}
                        onChange={(e) => onChange({ ...t, template_reminder: e.target.value })}
                        className="w-full h-8 text-sm border rounded px-2">
                <option value="">— pick —</option>
                {initialTpls.filter((x) => x.kind === "reminder").map((x) => (
                  <option key={x.id} value={x.id}>{x.name}</option>
                ))}
              </select>
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!t.active}
                    onChange={(e) => onChange({ ...t, active: e.target.checked })}
                    className="h-4 w-4 accent-indigo-600"
                    data-testid="trigger-editor-active" />
            Active — start creating candidates for this trigger
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button onClick={onSave} data-testid="trigger-editor-save">
            <Save className="h-3.5 w-3.5 mr-1" /> Save trigger
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function Field({ label, children }) {
  return (
    <div>
      <label className="text-2xs uppercase text-zinc-500">{label}</label>
      {children}
    </div>
  );
}


/* ------------------------------------------------------------------------ */
/*  Settings                                                                 */
/* ------------------------------------------------------------------------ */
function SettingsPane() {
  const [s, setS] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [testTo, setTestTo] = useState("");
  const [testId, setTestId] = useState("");
  const [busy, setBusy] = useState(false);
  const [editingTpl, setEditingTpl] = useState(null);

  const load = useCallback(async () => {
    try {
      const [r, tp] = await Promise.all([
        api.get("/reviews/settings"),
        api.get("/reviews/templates"),
      ]);
      setS(r.data);
      setTemplates(tp.data?.templates || []);
    } catch (e) {
      toast.error("Load failed", { description: e?.response?.data?.detail || e.message });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function save() {
    setBusy(true);
    try {
      await api.patch("/reviews/settings", {
        review_url: s.review_url,
        business_profile_id: s.business_profile_id,
        cooldown_days_default: parseInt(s.cooldown_days_default, 10) || 365,
        quiet_hours_start: s.quiet_hours_start,
        quiet_hours_end: s.quiet_hours_end,
        daily_cap_global: parseInt(s.daily_cap_global, 10) || 30,
        reminder_default_days: parseInt(s.reminder_default_days, 10) || 7,
        unsubscribe_text_sms: s.unsubscribe_text_sms,
        unsubscribe_text_email: s.unsubscribe_text_email,
        manager_test_send_to: s.manager_test_send_to,
        enabled: !!s.enabled,
      });
      toast.success("Settings saved");
      await load();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  }
  async function testSend() {
    if (!testId || !testTo) {
      toast.error("Pick a template + destination for the test send");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/reviews/templates/test-send", {
        template_id: testId, to: testTo, sample_first_name: "Sam",
      });
      toast.success(r.data?.ok ? "Test sent" : `Test send returned ${r.data?.info?.status || "?"}`);
    } catch (e) {
      toast.error("Test send failed", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  }
  async function saveTpl() {
    try {
      const body = {
        name: editingTpl.name, channel: editingTpl.channel,
        kind: editingTpl.kind, subject: editingTpl.subject,
        body: editingTpl.body,
      };
      if (editingTpl.__new) {
        await api.post("/reviews/templates", body);
      } else {
        await api.patch(`/reviews/templates/${editingTpl.id}`, body);
      }
      toast.success("Template saved");
      setEditingTpl(null);
      await load();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    }
  }
  async function delTpl(t) {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await api.delete(`/reviews/templates/${t.id}`);
      await load();
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || e.message });
    }
  }
  function newTpl() {
    setEditingTpl({
      __new: true, name: "Custom template", channel: "sms", kind: "initial",
      subject: "", body: "Hi {first_name}, {google_review_link}",
    });
  }

  if (!s) return <div className="text-sm text-zinc-500 py-8 text-center">
    <Loader2 className="h-4 w-4 inline mr-1 animate-spin" /> Loading…
  </div>;

  return (
    <div className="mt-4 space-y-4">
      <Card>
        <CardHeader><CardTitle className="text-base">Business profile</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Field label="Google review URL (https://g.page/…, https://maps.app.goo.gl/…, etc.)">
            <Input value={s.review_url || ""} onChange={(e) => setS({ ...s, review_url: e.target.value })}
                    placeholder="https://g.page/your-business" data-testid="settings-review-url" />
          </Field>
          <Field label="Google Business Profile ID (optional)">
            <Input value={s.business_profile_id || ""}
                    onChange={(e) => setS({ ...s, business_profile_id: e.target.value })} />
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Send policy</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Field label="Cooldown default (days)">
              <Input type="number" value={s.cooldown_days_default}
                      onChange={(e) => setS({ ...s, cooldown_days_default: e.target.value })} />
            </Field>
            <Field label="Daily cap (global)">
              <Input type="number" value={s.daily_cap_global}
                      onChange={(e) => setS({ ...s, daily_cap_global: e.target.value })} />
            </Field>
            <Field label="Quiet hours start (AU)">
              <Input value={s.quiet_hours_start || ""}
                      onChange={(e) => setS({ ...s, quiet_hours_start: e.target.value })}
                      placeholder="20:00" />
            </Field>
            <Field label="Quiet hours end (AU)">
              <Input value={s.quiet_hours_end || ""}
                      onChange={(e) => setS({ ...s, quiet_hours_end: e.target.value })}
                      placeholder="09:00" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Reminder default (days)">
              <Input type="number" value={s.reminder_default_days}
                      onChange={(e) => setS({ ...s, reminder_default_days: e.target.value })} />
            </Field>
            <Field label="Manager test-send to (email or phone)">
              <Input value={s.manager_test_send_to || ""}
                      onChange={(e) => setS({ ...s, manager_test_send_to: e.target.value })} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Unsubscribe text (SMS)">
              <Input value={s.unsubscribe_text_sms || ""}
                      onChange={(e) => setS({ ...s, unsubscribe_text_sms: e.target.value })}
                      placeholder="Reply STOP to unsubscribe." />
            </Field>
            <Field label="Unsubscribe text (email)">
              <Input value={s.unsubscribe_text_email || ""}
                      onChange={(e) => setS({ ...s, unsubscribe_text_email: e.target.value })} />
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!s.enabled}
                    onChange={(e) => setS({ ...s, enabled: e.target.checked })}
                    className="h-4 w-4 accent-indigo-600"
                    data-testid="settings-enabled" />
            <span>
              <b>Enable sending.</b> Turn off to pause every outbound message
              even when a manager has approved it.
            </span>
          </label>
          <Button onClick={save} disabled={busy} data-testid="settings-save">
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
            Save settings
          </Button>
        </CardContent>
      </Card>

      <RejectReasonsCard />

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Templates</CardTitle>
          <Button size="sm" onClick={newTpl} data-testid="templates-new">
            <Plus className="h-3.5 w-3.5 mr-1" /> New
          </Button>
        </CardHeader>
        <CardContent className="space-y-2">
          {templates.map((t) => (
            <div key={t.id} className="border rounded p-2 flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate flex items-center gap-1">
                  {t.name}
                  <Badge variant="outline" className="text-2xs">{t.channel}</Badge>
                  <Badge variant="outline" className="text-2xs">{t.kind}</Badge>
                  {t.is_default && <Badge variant="outline" className="text-2xs">default</Badge>}
                </div>
                <div className="text-2xs text-zinc-500 truncate">{t.body?.slice(0, 90)}…</div>
              </div>
              <Button size="sm" variant="outline" onClick={() => setEditingTpl({ ...t })}
                      data-testid={`template-edit-${t.id}`}>
                <Edit3 className="h-3.5 w-3.5" />
              </Button>
              {!t.is_default && (
                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => delTpl(t)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          ))}
          <div className="border-t pt-2 mt-2 space-y-1">
            <div className="text-2xs uppercase text-zinc-500">Test send</div>
            <div className="flex gap-2">
              <select value={testId} onChange={(e) => setTestId(e.target.value)}
                        className="flex-1 h-8 text-sm border rounded px-2">
                <option value="">— template —</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              <Input value={testTo} onChange={(e) => setTestTo(e.target.value)}
                      placeholder="Phone or email" className="flex-1" />
              <Button variant="outline" onClick={testSend} disabled={busy}
                      data-testid="templates-test-send">
                <Send className="h-3.5 w-3.5 mr-1" /> Send test
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {editingTpl && (
        <Dialog open onOpenChange={() => setEditingTpl(null)}>
          <DialogContent className="max-w-2xl w-[95vw]">
            <DialogHeader>
              <DialogTitle>{editingTpl.__new ? "New template" : `Edit — ${editingTpl.name}`}</DialogTitle>
              <DialogDescription>
                Use <code>{"{first_name}"}</code>, <code>{"{google_review_link}"}</code>,
                and <code>{"{unsubscribe_text}"}</code> placeholders.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Field label="Name">
                <Input value={editingTpl.name} onChange={(e) => setEditingTpl({ ...editingTpl, name: e.target.value })} />
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Channel">
                  <select value={editingTpl.channel}
                            onChange={(e) => setEditingTpl({ ...editingTpl, channel: e.target.value })}
                            className="w-full h-8 text-sm border rounded px-2"
                            disabled={editingTpl.is_default}>
                    <option value="sms">SMS</option>
                    <option value="email">Email</option>
                  </select>
                </Field>
                <Field label="Kind">
                  <select value={editingTpl.kind}
                            onChange={(e) => setEditingTpl({ ...editingTpl, kind: e.target.value })}
                            className="w-full h-8 text-sm border rounded px-2"
                            disabled={editingTpl.is_default}>
                    <option value="initial">initial</option>
                    <option value="reminder">reminder</option>
                  </select>
                </Field>
              </div>
              {editingTpl.channel === "email" && (
                <Field label="Subject">
                  <Input value={editingTpl.subject || ""}
                          onChange={(e) => setEditingTpl({ ...editingTpl, subject: e.target.value })} />
                </Field>
              )}
              <Field label="Body">
                <Textarea rows={editingTpl.channel === "email" ? 12 : 6}
                            value={editingTpl.body || ""}
                            onChange={(e) => setEditingTpl({ ...editingTpl, body: e.target.value })}
                            data-testid="template-editor-body" />
                <div className="text-2xs text-zinc-500 mt-1">
                  Chars: {(editingTpl.body || "").length}
                </div>
              </Field>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setEditingTpl(null)}>Cancel</Button>
              <Button onClick={saveTpl} data-testid="template-editor-save">
                <Save className="h-3.5 w-3.5 mr-1" /> Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
